import React, { useState, useMemo, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useWizard } from '../../context/WizardContext';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';

const LanguageSelection = ({
  stepNumber = 1,
  totalSteps = TOTAL_WIZARD_STEPS,
  onNext,
  onPrevious,
  canGoPrevious = false
}) => {
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const { actions, state } = useWizard();

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);
  const [selectedLanguage, setSelectedLanguage] = useState(
    state.languagePreference?.selectedLanguage || currentLanguage || 'en'
  );

  const languageOptions = useMemo(() => ([
    {
      code: 'en',
      label: t('wizard.language.option.en.label', 'English'),
      nativeLabel: t('wizard.language.option.en.native', 'English'),
      description: t(
        'wizard.language.option.en.description',
        'Guided experience in international English, suitable for visa documentation.'
      ),
      badge: t('wizard.language.option.en.badge', 'Recommended for documentation')
    },
    {
      code: 'ta',
      label: t('wizard.language.option.ta.label', 'Tamil'),
      nativeLabel: t('wizard.language.option.ta.native', 'தமிழ்'),
      description: t(
        'wizard.language.option.ta.description',
        'நிகழ்வுகளை தமிழ் மொழியில் அனுபவிக்கவும். ஆலோசகர்களுடன் எளிதாக பேச உதவும்.'
      ),
      badge: t('wizard.language.option.ta.badge', 'தயாராகும் ஆலோசனை ஆதாரம்')
    },
    {
      code: 'si',
      label: t('wizard.language.option.si.label', 'Sinhala'),
      nativeLabel: t('wizard.language.option.si.native', 'සිංහල'),
      description: t(
        'wizard.language.option.si.description',
        'සිංහල භාෂාවෙන් මඟ පෙන්වීමක් ලබා ගන්න. ඔබගේ දුරකථන ඇමුණුම්වලට උපකාරී වේ.'
      ),
      badge: t('wizard.language.option.si.badge', 'අනුවාද සහාය ඇතුළත්')
    }
  ]), [t]);

  useEffect(() => {
    if (selectedLanguage !== currentLanguage) {
      actions.updateLanguageSelection({ selectedLanguage });
      changeLanguage(selectedLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const handleSelect = (code) => {
    setSelectedLanguage(code);
  };

  const handleNext = () => {
    if (onNext) {
      onNext({
        languagePreference: {
          selectedLanguage
        }
      });
    }
  };

  return (
    <div className="language-selection-step w-full mx-auto">
      <div className="text-center mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          {t('wizard.language.title', 'Choose your preferred language')}
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {t(
            'wizard.language.subtitle',
            'Select the language you are most comfortable with. You can switch anytime and we will send every document and follow-up in your chosen language.'
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {languageOptions.map((option) => {
          const isActive = selectedLanguage === option.code;
          return (
            <button
              key={option.code}
              onClick={() => handleSelect(option.code)}
              className={`flex flex-col h-full text-left border rounded-2xl p-5 transition-all duration-200 ${
                isActive
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm'
              }`}
              aria-pressed={isActive}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {option.label}
                  </p>
                  <p className="text-sm text-gray-500">{option.nativeLabel}</p>
                </div>
                {isActive && (
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center">
                    ✓
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-700 flex-1">{option.description}</p>
              <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                {option.badge}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex justify-between items-center">
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('wizard.common.previous', 'Previous')}
        </button>

        <button
          onClick={handleNext}
          disabled={!selectedLanguage}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {t('wizard.language.next', 'Continue')}
        </button>
      </div>
    </div>
  );
};

export default LanguageSelection;
